/**
 * App - Root application component with routing
 * 
 * CHANGELOG:
 * - 2026-03-05: Added ErrorBoundary wrapper to catch runtime crashes (prevents blank page)
 * - Previous: No error boundary, runtime errors caused blank page with no feedback
 */
import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { Home } from './pages/Home';
import { About } from './pages/About';
import { Listing } from './pages/Listing';
import { Collections } from './pages/Collections';
import { Services } from './pages/Services';
import { Contact } from './pages/Contact';
import { PropertyDetail } from './pages/PropertyDetail';
import { AreaPage } from './pages/AreaPage';
import { StyleGuide } from './pages/StyleGuide';
import { PrivacyPolicy } from './pages/PrivacyPolicy';
import { BookingTerms } from './pages/BookingTerms';
import { CookiePolicy } from './pages/CookiePolicy';
import { Owners } from './pages/Owners';
import { RouteMeta } from './components/RouteMeta';
import { SearchBarProvider } from './contexts/SearchBarContext';
import { ModalProvider } from './contexts/ModalContext';

import { ErrorBoundary } from './components/ErrorBoundary';
import { NotFound } from './pages/NotFound';
import { FloatingChatButton } from './components/FloatingChatButton';
import { ScrollToTop } from './components/ScrollToTop';
import { CookieBanner } from './components/CookieBanner';

/** Redirect legacy /detail/:id URLs to /property/:id */
function DetailRedirect() {
  const { id } = useParams();
  return <Navigate to={`/property/${id}`} replace />;
}

export default function App() {
  // Set scroll restoration to manual on app load
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
  }, []);

  return (
    <ErrorBoundary>
      <Router>
        <SearchBarProvider>
          <ModalProvider>
            <RouteMeta />
            <ScrollToTop />
            <div className="flex flex-col min-h-screen">
              <Header />
              <div className="flex-grow relative ui-z-content">
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/destinations" element={<Listing />} />
                  <Route path="/collections" element={<Collections />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/marbella-golden-mile" element={<AreaPage />} />
                  <Route path="/nueva-andalucia" element={<AreaPage />} />
                  <Route path="/puerto-banus" element={<AreaPage />} />
                  <Route path="/elviria" element={<AreaPage />} />
                  <Route path="/sierra-blanca" element={<AreaPage />} />
                  <Route path="/property/:id" element={<PropertyDetail />} />
                  <Route path="/detail/:id" element={<DetailRedirect />} />
                  <Route path="/style-guide" element={<StyleGuide />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/booking-terms" element={<BookingTerms />} />
                  <Route path="/cookie-policy" element={<CookiePolicy />} />
                  <Route path="/owners" element={<Owners />} />
                  {/* 404 for unknown routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </div>
              <Footer />
              <FloatingChatButton />
              <CookieBanner />
            </div>
          </ModalProvider>
        </SearchBarProvider>
      </Router>
    </ErrorBoundary>
  );
}
